import React, { useEffect, useMemo, useState } from 'react';
import CustomText from '../../../../Common/customComponents/CustomText.tsx';
import { TextButton } from '../../../../../components';
import ButtonInnerX from '/assets/icon/global/button-inner-x.svg';
import UserSearchModal from '../../../Setting/modal/UserSearchModal.tsx';
import { SignatureFormProps, SignatureModal } from '../../../../SignatureModal';
import PtwSignatureModal from '../../../MyApproval/modal/PtwSignatureModal.tsx';
import { ptwApi } from '../../../../../api/ptw.api.ts';
import { commonMsg } from '../../../../../utils/globalUtil.tsx';
import { useAlarm, useUser } from '../../../../../hooks';
import { usePermitIssue } from 'containers/ptw/contexts/PermitIssueContext.ts';
import { usePtwDetailModal } from '../../../contexts/PtwDetailModalContext.ts';
import { useStatusUpdateDate } from '../../../contexts/PermitStatusUpdateDate.ts';

interface IProps {
  data: any;
  setData: any;
  type: 'ISSUE' | 'DAILY' | 'CLOSURE' | 'EXTEND';
  approverItem: any;
  checkedSeq: any;
  setCheckedSeq: any;
  isReadOnly: boolean;
  onRefresh?: () => void;
  isFinish: boolean;
  disableSearch?: boolean;
}

const FormApprovalRow = (props: IProps) => {
  const { showAlarm, closeAlarm } = useAlarm();
  const user = useUser();
  const {
    isPtwDetailModalOpen,
    setIsPtwDetailModalOpen,
    setSelectedApprovalType,
    selectedApprovalType,
    autoOpenSignatureType,
    setAutoOpenSignatureType,
    saveBeforeApproval
  } = usePtwDetailModal();

  const [isUserSearchModalOpen, setIsUserSearchModalOpen] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const [selectedUserList, setSelectedUserList] = useState<any>([]);

  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState<boolean>(false);

  const [isSignReadonly, setIsSignReadonly] = useState<boolean>(false);
  const { pageId } = usePermitIssue();

  const { statusUpdateDate, setStatusUpdateDate } = useStatusUpdateDate();

  const getApprovalKey = (item: any): number | null => {
    return item?.approvalMSeq ?? item?.settingApprovalModeSeq ?? null;
  };

  const checkAction = (approverItem: any) => {
    const key = getApprovalKey(approverItem);
    props.setCheckedSeq((prev: any) => (prev === key ? null : key));
  };

  const onActionUserSearchCallback = (checkedList: any[]) => {
    if (!selectedSeq) return;

    props.setData((prev: any) => {
      const prevList = prev?.approvalMVOList ?? [];

      const nextList = prevList.map((m: any) => {
        if (getApprovalKey(m) !== selectedSeq) return m;

        const prevUsers: any[] = m?.ptwPageApprovalDVOList ?? [];
        const existIds = new Set(prevUsers.map((u: any) => u?.userId).filter(Boolean));

        const addUsers = (checkedList ?? [])
          .filter((u: any) => u?.userId && !existIds.has(u.userId))
          .map((u: any) => ({
            userId: u.userId,
            userNm: u.userNm
          }));

        return {
          ...m,
          ptwPageApprovalDVOList: [...prevUsers, ...addUsers]
        };
      });

      return { ...prev, approvalMVOList: nextList };
    });

    setSelectedSeq(null);
    setSelectedUserList([]);
  };

  const delUser = (approverItem: any, userId: string) => {
    const targetKey = getApprovalKey(approverItem);
    if (targetKey == null) return;

    props.setData((prev: any) => {
      const prevList: any[] = prev?.approvalMVOList ?? [];

      const nextList = prevList.map((m: any) => {
        if (getApprovalKey(m) !== targetKey) return m;

        const prevUsers: any[] = m?.ptwPageApprovalDVOList ?? [];
        const nextUsers = prevUsers.filter((u: any) => u?.userId !== userId);

        return { ...m, ptwPageApprovalDVOList: nextUsers };
      });

      return { ...prev, approvalMVOList: nextList };
    });
  };

  const onActionSignatureCallback = (
    approvalProcessSeq: number,
    approvalMSeq: number,
    ptwDSeq: number,
    signType: string,
    base64: string,
    comment: string,
    addUserList: any[],
    nextApprovalMSeq: number
  ) => {
    const formData = new FormData();
    formData.set('approvalProcessSeq', String(approvalProcessSeq));
    formData.set('approvalMSeq', String(approvalMSeq));
    formData.set('ptwDSeq', String(ptwDSeq));
    formData.set('signType', signType);
    formData.set('sign', base64);
    formData.set('nextApprovalMSeq', String(nextApprovalMSeq));
    formData.set('approvalType', props.type);

    if (comment != null && comment != '') {
      formData.set('comment', comment);
    }

    for (let i = 0; i < addUserList.length; i++) {
      const row = addUserList[i];
      for (const [k, v] of Object.entries(row)) {
        if (k == 'userId') {
          formData.append(`addUserList[${i}]`, String(v ?? ''));
        }
      }
    }

    ptwApi.setApprovalProcessSign(formData).then(async (r: any) => {
      if (r.resultObject > 0) {
        props.onRefresh?.();
        commonMsg.alert(showAlarm, closeAlarm, 'Success', 'update success', () => {
          setIsPtwDetailModalOpen(false);
        });
        props.setData((prev: any) => {
          const prevList: any[] = prev?.approvalMVOList ?? [];
          const nextList = prevList.map((m: any) => {
            if (m.approvalMSeq !== approvalMSeq) return m;
            return { ...m, signType: signType, sign: base64, comment: comment };
          });
          return { ...prev, approvalMVOList: nextList };
        });
        setStatusUpdateDate(new Date());
      } else {
        commonMsg.alert(showAlarm, closeAlarm, 'Error', 'Error', null);
      }
    });
  };

  const approvalRow = useMemo(() => {
    const key = getApprovalKey(props.approverItem);
    if (key == null) return undefined;

    return (props.data?.approvalMVOList ?? []).find((m: any) => getApprovalKey(m) === key);
  }, [props.data?.approvalMVOList, props.approverItem]);

  useEffect(() => {
    const isCurrentUser = (approvalRow?.ptwPageApprovalDVOList ?? []).some(
      (u: any) => u?.userId === user.userInfo?.userId
    );
    if (
      autoOpenSignatureType &&
      autoOpenSignatureType === props.approverItem?.approvalType &&
      props.disableSearch &&
      approvalRow?.approvalProcessSeq != null &&
      !approvalRow?.signType &&
      isCurrentUser
    ) {
      setIsSignReadonly(false);
      setIsSignatureModalOpen(true);
      setAutoOpenSignatureType(null);
    }
  }, [autoOpenSignatureType, approvalRow?.approvalProcessSeq]);

  const changeOnLine = () => {
    const targetKey = getApprovalKey(props.approverItem);
    if (targetKey == null) return;

    props.setData((prev: any) => {
      const prevList: any[] = prev?.approvalMVOList ?? [];

      const nextList = prevList.map((m: any) => {
        if (getApprovalKey(m) !== targetKey) return m;

        if (m.isOnline == true) {
          return { ...m, isOnline: false, ptwPageApprovalDVOList: [] };
        } else {
          return { ...m, isOnline: true };
        }
      });

      return { ...prev, approvalMVOList: nextList };
    });
  };

  const renderSignatureBlock = () => {
    /* my issue, my status, isFinish가 true 인경우 사인 불가 */
    if (pageId == 'issue' || pageId == 'status' || props.isFinish) {
      if (!approvalRow?.signType) {
        if (!approvalRow?.isOnline) {
          return (
            <div className={'sign-div'}>
              <span>Sign on Paper</span>
            </div>
          );
        } else {
          return (
            <div className={'sign-div'}>
              <span>Signature</span>
            </div>
          );
        }
      } else {
        return (
          <div
            className={'sign-div'}
            onClick={() => {
              setIsSignReadonly(true);
              setIsSignatureModalOpen(true);
            }}
          >
            {approvalRow.signType == 'DENY' && (
              <div>
                <span className={'sign-deny'}>DENY</span>
              </div>
            )}

            {approvalRow.signType == 'APPROVED' && (
              <img
                src={`data:image/png;base64,${approvalRow.sign}`}
                alt="signature"
                style={{ width: '100%', height: 'auto', maxWidth: 240 }}
              />
            )}
          </div>
        );
      }
    } else {
      if (!approvalRow.signType) {
        // 사인 가능(미 사인)
        if (approvalRow.approvalProcessSeq != null) {
          // 결재라인이 생성된 경우
          if (selectedApprovalType == props.approverItem.approvalType) {
            return (
              <div
                className={'sign-div active'}
                onClick={async () => {
                  if (saveBeforeApproval.current) {
                    const ok = await saveBeforeApproval.current();
                    if (!ok) return;
                  }
                  setIsSignReadonly(false);
                  setIsSignatureModalOpen(true);
                }}
              >
                <span>Signature</span>
              </div>
            );
          } else {
            return (
              <div className={'sign-div'}>
                <span>Signature</span>
              </div>
            );
          }
        } else {
          // 결재라인이 없는 경우(초기화면 또는 OFFLINE)
          if (!approvalRow.isOnline) {
            return (
              <div className={'sign-div'}>
                <span>Sign on Paper</span>
              </div>
            );
          } else {
            return (
              <div className={'sign-div'}>
                <span>Signature</span>
              </div>
            );
          }
        }
      } else {
        // 사인 완료
        return (
          <div
            className={'sign-div'}
            onClick={() => {
              setIsSignReadonly(true);
              setIsSignatureModalOpen(true);
            }}
          >
            {approvalRow.signType == 'DENY' && (
              <div>
                <span className={'sign-deny'}>DENY</span>
              </div>
            )}

            {approvalRow.signType == 'APPROVED' && (
              <img
                src={`data:image/png;base64,${approvalRow.sign}`}
                alt="signature"
                style={{ width: '100%', height: 'auto', maxWidth: 240 }}
              />
            )}
          </div>
        );
      }
    }
  };

  const isChangeApproval = () => {
    let value = true;

    if (!props.approverItem.isOnline) {
      return false;
    } else if (pageId == 'issue') {
      if (props.data?.ptwNo == null) {
        value = true;
      } else {
        value = false;
      }
    } else if (pageId == 'approval') {
      return false;
    } else {
      value = false;
    }
    return value;
  };

  return (
    <>
      <PtwSignatureModal
        setIsModalOpen={setIsSignatureModalOpen}
        isModalOpen={isSignatureModalOpen}
        onActionCallback={onActionSignatureCallback}
        approverItem={props.approverItem}
        isReadonly={isSignReadonly}
      />

      <UserSearchModal
        setIsModalOpen={setIsUserSearchModalOpen}
        isModalOpen={isUserSearchModalOpen}
        title={'Approval Authority'}
        onActionCallback={onActionUserSearchCallback}
        selectedList={selectedUserList}
        searchKeyword={searchKeyword}
        settingApproverMSeq={props?.approverItem?.settingApproverMSeq}
      />
      <tr>
        <td className={'text-align-center'} style={{ width: '50px' }}>
          {!props.isReadOnly && !props.disableSearch && (
            <input
              type={'checkbox'}
              className={'form-check'}
              checked={getApprovalKey(props.approverItem) === props.checkedSeq}
              onClick={(e) => e.stopPropagation()}
              onChange={() => {
                checkAction(props.approverItem);
              }}
            />
          )}
        </td>
        <th style={{ width: '250px' }}>{props.approverItem.approverCategory}</th>
        <td>
          <div className={'group-td-column'}>
            <div className={'approver-select-list'}>
              {(props.approverItem?.ptwPageApprovalDVOList ?? []).map(
                (userItem: any, userIndex: number) => (
                  <div className={'selected-item'} key={userIndex}>
                    <span>{userItem.userNm}</span>
                    {isChangeApproval() && !props.disableSearch && (
                      <button
                        onClick={() => {
                          delUser(props.approverItem, userItem.userId);
                        }}
                      >
                        <img src={ButtonInnerX} alt={'button inner x'} />
                      </button>
                    )}
                  </div>
                )
              )}
            </div>
            <div className={'group-td-row'}>
              <div style={{ flex: '1' }}>
                <CustomText
                  name={'searchKeyword'}
                  value={searchKeyword}
                  isBtnSearch={isChangeApproval() && !props.disableSearch}
                  onChange={(e: any) => {
                    setSearchKeyword(e.target.value);
                  }}
                  onSearchAction={() => {
                    setSelectedSeq(getApprovalKey(props.approverItem));
                    setSelectedUserList(props.approverItem.ptwPageApprovalDVOList);
                    setIsUserSearchModalOpen(true);
                  }}
                  isBtnClose={true}
                  readOnly={!isChangeApproval()}
                  maxLength={30}
                />
              </div>

              <div>
                <TextButton
                  variant="outlined"
                  onClick={() => {
                    alert();
                    changeOnLine();
                  }}
                  style={{ cursor: 'pointer' }}
                  disabled
                >
                  {props.approverItem.isOnline && 'online Sign'}
                  {!props.approverItem.isOnline && 'Offline Sign'}
                </TextButton>
              </div>
            </div>

            {/*사인화면*/}
            {renderSignatureBlock()}
          </div>
        </td>
      </tr>
    </>
  );
};

export default FormApprovalRow;
